'use client'

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { XIcon } from 'lucide-react'

import { useSubmit } from '@/components/Form/useSubmit'
import { cn } from '@/components/new/lib/utils'
import { Button, ButtonSizeContext } from '@/components/new/ui/button'
import { Input } from '@/components/new/ui/input'
import { usePopupLayerContainer } from '@/components/new/ui/popup-layer'

const DialogDismissalContext = React.createContext(false)
type DialogMobileLayout = 'content' | 'fullscreen'
const DialogLayoutContext = React.createContext<DialogMobileLayout | undefined>(undefined)

function Dialog({
    disableDismissal = false,
    onOpenChange,
    ...props
}: DialogPrimitive.Root.Props & { disableDismissal?: boolean }) {
    return (
        <DialogDismissalContext.Provider value={disableDismissal}>
            <DialogPrimitive.Root
                data-slot="dialog"
                disablePointerDismissal
                {...props}
                onOpenChange={(open, details) => {
                    if (!open && disableDismissal) {
                        details.cancel()
                        return
                    }

                    onOpenChange?.(open, details)
                }}
            />
        </DialogDismissalContext.Provider>
    )
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
    const popupLayerContainer = usePopupLayerContainer()

    return (
        <DialogPrimitive.Portal
            data-slot="dialog-portal"
            container={props.container ?? popupLayerContainer}
            {...props}
        />
    )
}

function DialogClose({
    hideOnMobile = false,
    className,
    disabled,
    ...props
}: DialogPrimitive.Close.Props & { hideOnMobile?: boolean }) {
    const mobileLayout = React.useContext(DialogLayoutContext)
    const disableDismissal = React.useContext(DialogDismissalContext)

    return (
        <DialogPrimitive.Close
            data-slot="dialog-close"
            {...props}
            disabled={disabled || disableDismissal}
            className={(state) =>
                cn(
                    typeof className === 'function' ? className(state) : className,
                    mobileLayout && hideOnMobile && 'dialog-mobile:hidden'
                )
            }
        />
    )
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
    return (
        <DialogPrimitive.Backdrop
            // Nested dialogs dim the dialog below them as well
            forceRender
            data-slot="dialog-overlay"
            className={cn(
                'supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 fixed inset-0 isolate z-50 bg-black/10 duration-100',
                className
            )}
            {...props}
        />
    )
}

function DialogContent({
    className,
    children,
    showCloseButton = true,
    mobileLayout,
    ...props
}: DialogPrimitive.Popup.Props & {
    showCloseButton?: boolean
    mobileLayout?: DialogMobileLayout
}) {
    const { t } = useTranslation()

    return (
        <DialogLayoutContext.Provider value={mobileLayout}>
            <DialogPortal>
                <DialogOverlay />
                <DialogPrimitive.Popup
                    data-slot="dialog-content"
                    className={cn(
                        'bg-popover text-popover-foreground ring-foreground/10 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 fixed left-1/2 top-1/2 z-50 grid max-h-[calc(100dvh-2rem)] w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-6 overflow-y-auto rounded-xl p-6 text-sm outline-none ring-1 duration-100 sm:max-w-md',
                        '[&_[data-slot=field-group].overflow-auto]:p-1 [&_[data-slot=field-group].overflow-y-auto]:p-1',
                        mobileLayout &&
                            'dialog-mobile:flex dialog-mobile:flex-col dialog-mobile:gap-3 dialog-mobile:overflow-hidden dialog-mobile:p-3',
                        mobileLayout === 'content' && 'dialog-mobile:h-auto',
                        mobileLayout === 'fullscreen' &&
                            'dialog-mobile:h-dvh dialog-mobile:max-h-dvh dialog-mobile:max-w-full! dialog-mobile:rounded-none dialog-mobile:pt-[max(0.75rem,env(safe-area-inset-top))] dialog-mobile:pr-[max(0.75rem,env(safe-area-inset-right))] dialog-mobile:pb-[max(0.75rem,env(safe-area-inset-bottom))] dialog-mobile:pl-[max(0.75rem,env(safe-area-inset-left))] dialog-mobile:ring-0',
                        className
                    )}
                    {...props}
                >
                    {/* A dialog opened from another dialog's footer must not inherit the footer's button size */}
                    <ButtonSizeContext.Provider value="default">{children}</ButtonSizeContext.Provider>
                    {showCloseButton && (
                        <DialogClose
                            hideOnMobile
                            render={<Button variant="ghost" className="absolute right-4 top-4" size="icon-sm" />}
                        >
                            <XIcon />
                            <span className="sr-only">{t('actions.close')}</span>
                        </DialogClose>
                    )}
                </DialogPrimitive.Popup>
            </DialogPortal>
        </DialogLayoutContext.Provider>
    )
}

function DialogHeader({
    className,
    children,
    showCloseButton = false,
    ...props
}: React.ComponentProps<'div'> & { showCloseButton?: boolean }) {
    const mobileLayout = React.useContext(DialogLayoutContext)
    const { t } = useTranslation()

    return (
        <div
            data-slot="dialog-header"
            className={cn(
                'flex shrink-0 flex-col gap-2',
                showCloseButton && 'flex-row items-start',
                mobileLayout &&
                    'dialog-mobile:flex-row dialog-mobile:items-start dialog-mobile:border-0 dialog-mobile:p-0',
                className
            )}
            {...props}
        >
            {mobileLayout || showCloseButton ? (
                <>
                    <div
                        className={cn(
                            'flex min-w-0 flex-1 flex-col gap-2',
                            !showCloseButton && 'dialog-mobile:flex contents'
                        )}
                    >
                        {children}
                    </div>
                    <DialogClose
                        render={<Button type="button" variant="ghost" size="icon-sm" />}
                        className={showCloseButton ? undefined : 'dialog-mobile:inline-flex hidden'}
                    >
                        <XIcon />
                        <span className="sr-only">{t('actions.close')}</span>
                    </DialogClose>
                </>
            ) : (
                children
            )}
        </div>
    )
}

function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
    const mobileLayout = React.useContext(DialogLayoutContext)

    return (
        <div
            data-slot="dialog-body"
            className={cn(
                '-m-1 min-h-0 overflow-y-auto p-1',
                mobileLayout &&
                    'dialog-mobile:-m-1 dialog-mobile:h-auto dialog-mobile:min-w-0 dialog-mobile:gap-3 dialog-mobile:overflow-y-auto dialog-mobile:overscroll-contain dialog-mobile:p-1',
                mobileLayout === 'content' && 'dialog-mobile:flex-auto',
                mobileLayout === 'fullscreen' && 'dialog-mobile:flex-1',
                className
            )}
            {...props}
        />
    )
}

function DialogFooter({
    className,
    showCloseButton = false,
    mobileLayout = 'stack',
    children,
    ...props
}: React.ComponentProps<'div'> & {
    showCloseButton?: boolean
    mobileLayout?: 'stack' | 'custom'
}) {
    const { t } = useTranslation()
    const dialogMobileLayout = React.useContext(DialogLayoutContext)

    return (
        <div
            data-slot="dialog-footer"
            className={cn(
                'flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end',
                dialogMobileLayout &&
                    'dialog-mobile:m-0 dialog-mobile:rounded-none dialog-mobile:border-0 dialog-mobile:bg-transparent dialog-mobile:p-0',
                dialogMobileLayout &&
                    mobileLayout === 'stack' &&
                    'dialog-mobile:flex-col-reverse! dialog-mobile:items-stretch dialog-mobile:[&>button]:h-auto dialog-mobile:[&>button]:min-h-11 dialog-mobile:[&>button]:w-full dialog-mobile:[&>button]:whitespace-normal',
                className
            )}
            {...props}
        >
            <ButtonSizeContext.Provider value="lg">
                {children}
                {showCloseButton && (
                    <DialogClose render={<Button variant="outline" />}>{t('actions.close')}</DialogClose>
                )}
            </ButtonSizeContext.Provider>
        </div>
    )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
    const mobileLayout = React.useContext(DialogLayoutContext)

    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn(
                'font-heading font-medium leading-none',
                mobileLayout && 'dialog-mobile:min-w-0 dialog-mobile:break-words dialog-mobile:leading-snug',
                className
            )}
            {...props}
        />
    )
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn(
                'text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground text-sm',
                className
            )}
            {...props}
        />
    )
}

type DialogPromptProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    title: string
    message?: React.ReactNode
    mode?: 'confirm' | 'prompt' | 'alert'
    contentClassName?: string
    disableDismissal?: boolean
    defaultValue?: string
    placeholder?: string
    confirmLabel?: string
    cancelLabel?: string
    confirmVariant?: React.ComponentProps<typeof Button>['variant']
    onConfirm: (value?: string) => void | Promise<void>
}

function DialogPrompt({
    open,
    onOpenChange,
    title,
    message,
    mode = 'confirm',
    contentClassName,
    disableDismissal = false,
    defaultValue = '',
    placeholder,
    confirmLabel,
    cancelLabel,
    confirmVariant = 'default',
    onConfirm,
}: DialogPromptProps) {
    const { t } = useTranslation()
    const [value, setValue] = React.useState(defaultValue)
    const { submit, isSubmitting } = useSubmit({ toastMode: 'error' })
    const isSubmittingRef = React.useRef(false)
    const resolvedConfirmLabel = confirmLabel ?? t('actions.confirm')
    const resolvedCancelLabel = cancelLabel ?? t('actions.cancel')

    React.useEffect(() => {
        if (open) {
            setValue(defaultValue)
        }
    }, [open, defaultValue])

    const canSubmit = mode === 'prompt' ? !!value.trim() : true

    const handleConfirm = async () => {
        if (!canSubmit || isSubmittingRef.current) {
            return
        }

        isSubmittingRef.current = true

        try {
            const submitted = await submit(async () => {
                await onConfirm(mode === 'prompt' ? value.trim() : undefined)
            })

            if (submitted) {
                onOpenChange(false)
            }
        } finally {
            isSubmittingRef.current = false
        }
    }

    return (
        <Dialog
            open={open}
            disablePointerDismissal={disableDismissal}
            onOpenChange={(nextOpen, eventDetails) => {
                if (
                    !nextOpen &&
                    (isSubmittingRef.current || (disableDismissal && eventDetails.reason !== 'close-press'))
                ) {
                    return
                }

                onOpenChange(nextOpen)
            }}
        >
            <DialogContent className={cn('sm:max-w-md', contentClassName)} showCloseButton={false}>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {message && <DialogDescription>{message}</DialogDescription>}
                </DialogHeader>

                {mode === 'prompt' && (
                    <Input
                        autoFocus
                        value={value}
                        placeholder={placeholder}
                        onChange={(event) => setValue(event.currentTarget.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault()
                                void handleConfirm()
                            }
                        }}
                    />
                )}

                <DialogFooter>
                    {mode !== 'alert' && (
                        <DialogClose
                            render={
                                <Button type="button" variant="outline" disabled={isSubmitting}>
                                    {resolvedCancelLabel}
                                </Button>
                            }
                        />
                    )}
                    <Button
                        type="button"
                        variant={confirmVariant}
                        autoFocus={mode === 'alert'}
                        onClick={() => void handleConfirm()}
                        disabled={!canSubmit || isSubmitting}
                    >
                        {resolvedConfirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPrompt,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
}
